import 'ag-grid-community/dist/styles/ag-grid.css'
import 'ag-grid-community/dist/styles/ag-theme-alpine.css'
import { Button, Layout, Result } from 'antd'
import React, { Suspense, lazy, useContext } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { ApolloClientWrapper, BackgroundJobProvider, MeContext, MeProvider } from '../api'
import { AbilityProvider } from '../api/components/Can'
import { IdleLogoutModal } from '../common/components/IdleLogoutModal'
import { PageLoading } from '../common/components/PageLoading'
import { Tracking } from '../tracking/Tracking'
import { CurvoConnectPage } from './CurvoConnect/CurvoConnectPage'
import { ManagePage, UserPage } from './Manage/ManagePage'
import { HomeRoute } from './components/HomeRoute'
import { MainSideBar } from './components/MainSideBar'
import { NonSupplierGuardedRoute } from './components/NonSupplierGuardedRoute'
import { SupplierPortalGuardedRoute } from './components/SupplierPortalGuardedRoute'

type LegacyRedirectProps = {
  from: string
  to: string
}

const ActivateLazy = lazy(() => import('./Activate/Activate'))
const AnalyzeLazy = lazy(() => import('./Analyze/Analyze'))
const EnrichLazy = lazy(() => import('./Enrich/Enrich'))
const ProjectApertureLazy = lazy(() => import('./ProjectAperture/ProjectAperture'))
const SupplierPortalLazy = lazy(() => import('./SupplierPortal/SupplierPortal'))

const LegacyRedirect: React.FC<LegacyRedirectProps> = ({ from, to }) => {
  const location = useLocation()
  const queryString = window.location.search
  return <Navigate to={`${location.pathname.replace(from, to)}${queryString || ''}`} />
}

export const MainRoutes: React.FC = () => {
  const { data } = useContext(MeContext)
  return (
    <Routes>
      <Route path="/auth" element={<Navigate to="/" />} />
      <Route path="/analyze/*" element={<LegacyRedirect from="/analyze" to="/analytics" />} />
      <Route path="/activate/*" element={<LegacyRedirect from="/activate" to="/strategic-sourcing" />} />
      <Route path="/manage/*" element={<ManagePage />} />
      <Route path="/project-aperture/*" element={<CurvoConnectPage />} />
      {/* TODO: This probably needs its own component */}
      <Route path="/users/*" element={<UserPage />} />

      {/* #7701: the four hospital families are closed to a supplier-typed non-admin, so route
          reachability agrees with the nav. The wrapper is pass-through for everyone else. */}
      <Route
        path="/enrich/*"
        element={
          <NonSupplierGuardedRoute>
            <EnrichLazy />
          </NonSupplierGuardedRoute>
        }
      />
      <Route
        path="/analytics/*"
        element={
          <NonSupplierGuardedRoute>
            <AnalyzeLazy />
          </NonSupplierGuardedRoute>
        }
      />
      <Route
        path="/strategic-sourcing/*"
        element={
          <NonSupplierGuardedRoute>
            <ActivateLazy />
          </NonSupplierGuardedRoute>
        }
      />
      <Route
        path="/aperture/*"
        element={
          <NonSupplierGuardedRoute>
            <ProjectApertureLazy />
          </NonSupplierGuardedRoute>
        }
      />
      <Route
        path="/supplier-portal/*"
        element={
          <SupplierPortalGuardedRoute>
            <SupplierPortalLazy />
          </SupplierPortalGuardedRoute>
        }
      />
      {!data?.me?.isAdmin && (
        <>
          {/* #7701: these three duplicate the unconditional registrations above, which match first,
              so they are inert today. They are wrapped all the same — an unwrapped duplicate left
              behind would silently reopen the family if a later edit ever made it live. */}
          <Route
            path="/enrich/*"
            element={
              <NonSupplierGuardedRoute>
                <EnrichLazy />
              </NonSupplierGuardedRoute>
            }
          />
          <Route
            path="/analytics/*"
            element={
              <NonSupplierGuardedRoute>
                <AnalyzeLazy />
              </NonSupplierGuardedRoute>
            }
          />
          <Route
            path="/strategic-sourcing/*"
            element={
              <NonSupplierGuardedRoute>
                <ActivateLazy />
              </NonSupplierGuardedRoute>
            }
          />
        </>
      )}
      <Route path="/" element={<HomeRoute />} />
      <Route
        path="*"
        element={
          <Result
            status="404"
            title="404"
            subTitle="Sorry, the page you visited does not exist."
            extra={
              <Button type="primary" href="/">
                Back Home
              </Button>
            }
          />
        }
      />
    </Routes>
  )
}

export const Main: React.FC<{}> = () => {
  return (
    <ApolloClientWrapper>
      <MeProvider>
        <AbilityProvider>
          <BackgroundJobProvider>
            <Tracking />
            <IdleLogoutModal />
            <Layout style={{ height: '100vh', overflow: 'hidden' }}>
              <MainSideBar />
              <Suspense fallback={<PageLoading />}>
                <MainContent>
                  <MainRoutes />
                </MainContent>
              </Suspense>
            </Layout>
          </BackgroundJobProvider>
        </AbilityProvider>
      </MeProvider>
    </ApolloClientWrapper>
  )
}

const MainContent = styled.div`
  flex: 1;
  overflow: auto;
`
